// Ecran de premiere connexion : l'utilisateur choisit son profil, athlete
// (gratuit) ou coach (payant, non facture pour l'instant). Le choix est
// definitif : le serveur refuse ensuite tout changement (RoleService.choisirRole).
import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { choisirProfil, type Moi, type Role } from './moi.ts';

type Offre = { role: Role; titre: string; prix: string; description: string[] };

// Offres proposees a la premiere connexion (aussi affichees dans Mes preferences).
export const OFFRES: Offre[] = [
  {
    role: 'ATHLETE',
    titre: 'Athlète',
    prix: 'Gratuit',
    description: [
      'Consulte ton calendrier et tes séances du jour',
      'Allures, temps de passage, journal d’entraînement',
      'Peut être suivi par un coach',
    ],
  },
  {
    role: 'COACH',
    titre: 'Coach',
    prix: 'Payant (offert pour l’instant)',
    description: [
      'Tout ce que fait l’athlète',
      'Crée tes séances et établis les calendriers',
      'Suis tes athlètes et partage une bibliothèque avec d’autres coachs',
    ],
  },
];

export default function ChoixProfil({ moi, handleLogout }: { moi: Moi; handleLogout: () => void }) {
  const [choix, setChoix] = React.useState<Role | null>(null);
  const [enCours, setEnCours] = React.useState(false);
  const [erreur, setErreur] = React.useState<string | null>(null);

  const valider = async () => {
    if (!choix) return;
    setEnCours(true);
    setErreur(null);
    try {
      // Met a jour le cache et previent useMoi : l'application s'affiche.
      await choisirProfil(choix);
    } catch (e) {
      setErreur((e as Error).message);
      setEnCours(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        backgroundImage: 'url(/background.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <Box
        sx={{
          width: 'min(100%, 720px)',
          p: { xs: 2.5, sm: 4 },
          borderRadius: 3,
          color: '#fff',
          background: 'rgba(15, 20, 30, 0.72)',
          backdropFilter: 'blur(6px)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          Bienvenue{moi.nom ? ' ' + moi.nom : ''} !
        </Typography>
        <Typography sx={{ mb: 3, opacity: 0.85 }}>
          Pour commencer, choisis ton profil.
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
          {OFFRES.map((o) => {
            const selectionne = choix === o.role;
            return (
              <Card
                key={o.role}
                sx={{
                  flex: 1,
                  outline: selectionne ? '3px solid' : '1px solid transparent',
                  outlineColor: selectionne ? 'primary.main' : 'transparent',
                }}
              >
                <CardActionArea onClick={() => setChoix(o.role)} disabled={enCours} sx={{ height: '100%' }}>
                  <CardContent>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography variant="h6">{o.titre}</Typography>
                      <Chip size="small" color={o.role === 'COACH' ? 'primary' : 'success'} label={o.prix} />
                    </Stack>
                    {o.description.map((d) => (
                      <Typography key={d} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        • {d}
                      </Typography>
                    ))}
                  </CardContent>
                </CardActionArea>
              </Card>
            );
          })}
        </Stack>

        {erreur && <Alert severity="error" sx={{ mb: 2 }}>{erreur}</Alert>}

        <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
          Ce choix est définitif : il ne pourra plus être modifié depuis l’application.
        </Typography>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button color="inherit" onClick={handleLogout} disabled={enCours}>
            Se déconnecter
          </Button>
          <Button variant="contained" onClick={valider} disabled={!choix || enCours}>
            {choix ? `Continuer en ${choix === 'COACH' ? 'coach' : 'athlète'}` : 'Choisis un profil'}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
